import {
  useCreateTopicMutation,
  useGetTopicsQuery,
  usePatchTopicMutation,
  usePublishTopicQuestionsMutation,
  useUnpublishTopicQuestionsMutation,
} from "@api/quiz";
import { IconWithTooltip } from "@components/IconWithTooltip";
import {
  CheckIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  XMarkIcon,
} from "@heroicons/react/16/solid";
import { useToastEffect } from "@hooks/hooks";
import { CreateTopicDTO, PatchTopicDTO } from "@onetutor/shared/dto";
import { PublishStatus } from "@onetutor/shared/type";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface TopicSelectorProps {
  courseId: string;
  selectedTopicId: string | undefined;
  onTopicChange: (topicId: string) => void;
  creationEnabled?: boolean;
}

type TopicsType = {
  id: string;
  name: string;
  showInSearch: boolean;
  isEditing?: boolean;
  editName?: string;
  publishStatus: PublishStatus | "loading";
};

interface PublishStatusIconProps {
  topic: TopicsType;
  handlePublishToggle: (topicId: string) => void;
}

function PublishStatusIcon({
  topic,
  handlePublishToggle,
}: PublishStatusIconProps) {
  const commonClasses =
    "btn btn-xs btn-ghost mx-1 opacity-50 group-hover:opacity-100";
  const { t } = useTranslation(["quizManagementPage"]);

  if (topic.publishStatus === "loading") {
    return (
      <IconWithTooltip
        className={commonClasses}
        icon={<span className="loading loading-spinner loading-xs h-3 w-3" />}
        tooltip={t("tooltipPublishQuestions")}
        disabled={true}
        handler={() => handlePublishToggle(topic.id)}
        tooltipPosition="left"
      />
    );
  }

  if (topic.publishStatus === "all") {
    return (
      <IconWithTooltip
        className={commonClasses}
        icon={<EyeIcon className="h-3 w-3 text-success" />}
        tooltip={t("tooltipUnpublishAllQuestions")}
        handler={() => handlePublishToggle(topic.id)}
        tooltipPosition="left"
      />
    );
  }

  if (topic.publishStatus === "some") {
    return (
      <IconWithTooltip
        className={commonClasses}
        icon={<EyeSlashIcon className="h-3 w-3 text-warning" />}
        tooltip={t("tooltipPublishAllQuestions")}
        handler={() => handlePublishToggle(topic.id)}
        tooltipPosition="left"
      />
    );
  }

  return (
    <IconWithTooltip
      className={commonClasses}
      icon={<EyeSlashIcon className="h-3 w-3 text-base-300" />}
      tooltip={t("tooltipPublishAllQuestions")}
      disabled={false}
      handler={() => handlePublishToggle(topic.id)}
      tooltipPosition="left"
    />
  );
}

export function TopicSelector({
  courseId,
  selectedTopicId,
  onTopicChange,
  creationEnabled = false,
}: TopicSelectorProps) {
  const { t } = useTranslation(["quizManagementPage", "commons"]);

  const [topics, setTopics] = useState<TopicsType[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const {
    data: topicsData,
    error: topicsError,
    isError: isTopicsError,
  } = useGetTopicsQuery({ courseId, queryArgs: { includeUnpublished: true } });

  const [createTopic] = useCreateTopicMutation();
  const [patchTopic] = usePatchTopicMutation();
  const [publishTopicQuestions] = usePublishTopicQuestionsMutation();
  const [unpublishTopicQuestions] = useUnpublishTopicQuestionsMutation();

  useEffect(() => {
    if (topicsData?.topics) {
      setTopics(
        topicsData.topics.map(
          (topic): TopicsType => ({
            id: topic.id,
            name: topic.name,
            showInSearch: true,
            isEditing: false,
            editName: topic.name,
            publishStatus: topic.publishStatus ? topic.publishStatus : "none",
          }),
        ),
      );
    }
  }, [topicsData]);

  useToastEffect({ error: topicsError, isError: isTopicsError });

  const handleCreateTopic = async () => {
    if (!newName.trim()) {
      return;
    }
    setCreating(true);
    try {
      const dto: CreateTopicDTO = { name: newName.trim() };
      const res = await createTopic({ courseId, createTopicData: dto });
      if (res.data) {
        onTopicChange(res.data.id);
        setNewName("");
      }
    } finally {
      setCreating(false);
    }
  };

  const handleEditTopic = (topicId: string) => {
    setTopics((prev) =>
      prev.map((topic) =>
        topic.id === topicId
          ? { ...topic, isEditing: true, editName: topic.name }
          : { ...topic, isEditing: false },
      ),
    );
  };

  const handleCancelEdit = (topicId: string) => {
    setTopics((prev) =>
      prev.map((topic) =>
        topic.id === topicId
          ? { ...topic, isEditing: false, editName: topic.name }
          : topic,
      ),
    );
  };

  const handleSaveEdit = async (topicId: string) => {
    const topic = topics.find((t) => t.id === topicId);
    if (!topic?.editName?.trim()) {
      return;
    }

    try {
      const dto: PatchTopicDTO = { name: topic.editName.trim() };
      const res = await patchTopic({
        courseId,
        topicId,
        patchTopicData: dto,
      });

      if (res.data) {
        setTopics((prev) =>
          prev.map((t) =>
            t.id === topicId
              ? {
                  ...t,
                  name: res.data.name,
                  isEditing: false,
                  editName: res.data.name,
                }
              : t,
          ),
        );
      }
    } catch {
      handleCancelEdit(topicId);
    }
  };

  const handleEditNameChange = (topicId: string, newName: string) => {
    setTopics((prev) =>
      prev.map((topic) =>
        topic.id === topicId ? { ...topic, editName: newName } : topic,
      ),
    );
  };

  const handlePublishToggle = async (topicId: string) => {
    const topic = topics.find((t) => {
      return t.id === topicId;
    });
    if (!topic) {
      return;
    }

    const newPublishState = topic.publishStatus !== "all";

    setTopics((prev) =>
      prev.map((t) =>
        t.id === topicId ? { ...t, publishStatus: "loading" } : t,
      ),
    );

    try {
      if (newPublishState === true) {
        await publishTopicQuestions({ courseId, topicId });
      } else {
        await unpublishTopicQuestions({ courseId, topicId });
      }

      setTopics((prev) =>
        prev.map((t) =>
          t.id === topicId
            ? { ...t, publishStatus: newPublishState ? "all" : "none" }
            : t,
        ),
      );
    } catch {
      setTopics((prev) =>
        prev.map((t) =>
          t.id === topicId ? { ...t, publishStatus: topic.publishStatus } : t,
        ),
      );
    }
  };

  return (
    <div className="flex flex-col">
      <label htmlFor="topic" className="self-start">
        <span className="font-semibold text-lg">{t("topic")}</span>
      </label>
      <div className="mt-sm mb-sm flex w-full max-w-3xl gap-2">
        <label className="input input-sm sm:input-md input-bordered flex flex-1 items-center rounded-xl">
          <input
            type="text"
            className="grow"
            placeholder={t("placeholderSearch")}
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setTopics((prev) =>
                prev.map((t) => ({
                  ...t,
                  showInSearch: t.name
                    .toLowerCase()
                    .includes(e.target.value.toLowerCase()),
                })),
              );
            }}
          />
          <MagnifyingGlassIcon className="h-6 w-6 sm:h-7 sm:w-7" />
        </label>
        {creationEnabled && (
          <>
            <input
              type="text"
              placeholder={t("newTopicNamePlaceholder")}
              className="input input-sm sm:input-md input-bordered flex-1"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={50}
              minLength={1}
            />
            <button
              type="button"
              className="btn btn-sm sm:btn-md btn-primary"
              onClick={handleCreateTopic}
              disabled={!newName.trim() || creating}
            >
              {creating ? (
                <span className="loading loading-spinner loading-xs" />
              ) : (
                t("createTopic")
              )}
            </button>
          </>
        )}
      </div>
      <div className="h-60 max-h-60 w-full max-w-3xl overflow-y-auto rounded-lg border border-base-300 p-1">
        <div className="grid grid-cols-1 gap-2">
          {topics
            .filter((t) => t.showInSearch)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((topic) => (
              <div
                key={topic.id}
                className={`flex items-center ${topic.isEditing ? "rounded" : ""}`}
              >
                {topic.isEditing ? (
                  <>
                    <input
                      type="text"
                      className="input w-full"
                      value={topic.editName || ""}
                      onChange={(e) =>
                        handleEditNameChange(topic.id, e.target.value)
                      }
                      maxLength={50}
                      minLength={1}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSaveEdit(topic.id);
                        } else if (e.key === "Escape") {
                          handleCancelEdit(topic.id);
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-xs btn-success mx-1"
                      onClick={() => handleSaveEdit(topic.id)}
                      disabled={!topic.editName?.trim()}
                    >
                      <CheckIcon className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-ghost mx-1"
                      onClick={() => handleCancelEdit(topic.id)}
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </>
                ) : (
                  <div className="group flex w-full items-center">
                    <button
                      type="button"
                      className={`btn btn-sm flex-1 justify-start text-left ${topic.id === selectedTopicId ? "btn-primary" : "border-2 hover:border-primary"}`}
                      onClick={() =>
                        onTopicChange(
                          topic.id === selectedTopicId ? "" : topic.id,
                        )
                      }
                    >
                      {topic.name}
                    </button>

                    <PublishStatusIcon
                      topic={topic}
                      handlePublishToggle={handlePublishToggle}
                    />

                    <button
                      type="button"
                      className="btn btn-xs btn-ghost mx-1 opacity-50 group-hover:opacity-100"
                      onClick={() => handleEditTopic(topic.id)}
                    >
                      <PencilIcon className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
